import React, { useMemo } from 'react'
import { AppTable, FlexBox } from 'components'
import { Button, Card } from 'semantic-ui-react'
import { useTabletAndBelowMediaQuery } from 'layout'
import { PencilSimple, Pause, Play, Warning } from 'phosphor-react'
import {
    formatFiltersLabel,
    formatUtcInTimezone,
    getViewerTimezone,
    getLastRunBadge,
} from './consolidated-report-email-utils'
import { HoldBadge, StatusBadge } from './consolidated-report-email-styles'

const ActionButtons = ({ item, onEdit, onHoldToggle, onFireNow }) => {
    const onHold = item.OnHold === true
    return (
        <FlexBox className="justify-content-center gap-1">
            <Button
                icon
                basic
                size="mini"
                title="Edit"
                onClick={() => onEdit(item)}
            >
                <PencilSimple size={16} />
            </Button>
            <Button
                icon
                basic
                size="mini"
                title={onHold ? 'Resume' : 'Hold'}
                onClick={() => onHoldToggle(item, !onHold)}
            >
                {onHold ? <Play size={16} /> : <Pause size={16} />}
            </Button>
            <Button
                icon
                basic
                size="mini"
                title="Fire Now"
                onClick={() => onFireNow(item)}
                style={{ color: '#dc2626' }}
            >
                <Warning size={16} weight="fill" />
            </Button>
        </FlexBox>
    )
}

const ScheduleCard = ({ item, onEdit, onHoldToggle, onFireNow, timezone }) => {
    const lastBadge = getLastRunBadge(item.LastRunStatus)
    const viewerTimezone = getViewerTimezone(timezone)
    return (
        <Card fluid>
            <Card.Content>
                <Card.Header>
                    {item.ScheduleName}
                    {item.OnHold && (
                        <HoldBadge $color="orange">On Hold</HoldBadge>
                    )}
                </Card.Header>
                <Card.Meta>{item.CadenceLabel}</Card.Meta>
                <Card.Description>
                    <div>{formatFiltersLabel(item)}</div>
                    <div>Recipients: {item.RecipientCount || 0}</div>
                    {item.NextRunUtc && (
                        <div>
                            Next:{' '}
                            {formatUtcInTimezone(
                                item.NextRunUtc,
                                viewerTimezone,
                            )}
                        </div>
                    )}
                    {item.LastRunStatus && (
                        <StatusBadge $color={lastBadge.color}>
                            {lastBadge.label}
                            {item.LastRunReports != null
                                ? ` · ${item.LastRunReports} reports`
                                : ''}
                        </StatusBadge>
                    )}
                </Card.Description>
                <FlexBox className="justify-content-end mt-2">
                    <ActionButtons
                        item={item}
                        onEdit={onEdit}
                        onHoldToggle={onHoldToggle}
                        onFireNow={onFireNow}
                    />
                </FlexBox>
            </Card.Content>
        </Card>
    )
}

export const ConsolidatedScheduleList = ({
    data,
    filter,
    pageChanged,
    totalCount,
    timezone,
    onEdit,
    onHoldToggle,
    onFireNow,
}) => {
    const isMobile = useTabletAndBelowMediaQuery()

    const columns = useMemo(
        () => [
            {
                title: 'Name',
                render: item => (
                    <span>
                        <strong>{item.ScheduleName}</strong>
                        {item.OnHold && (
                            <HoldBadge $color="orange">On Hold</HoldBadge>
                        )}
                    </span>
                ),
            },
            {
                title: 'Cadence',
                render: item => <span>{item.CadenceLabel || '—'}</span>,
            },
            {
                title: 'Filters',
                render: item => <span>{formatFiltersLabel(item)}</span>,
            },
            {
                title: 'Recipients',
                render: item => <span>{item.RecipientCount || 0}</span>,
            },
            {
                title: 'Next run',
                render: item => (
                    <span>
                        {item.NextRunUtc
                            ? formatUtcInTimezone(
                                  item.NextRunUtc,
                                  getViewerTimezone(timezone),
                              )
                            : '—'}
                    </span>
                ),
            },
            {
                title: 'Last run',
                render: item => {
                    const badge = getLastRunBadge(item.LastRunStatus)
                    if (!item.LastRunStatus) {
                        return item.OnHold ? (
                            <StatusBadge $color="grey">Held</StatusBadge>
                        ) : (
                            '—'
                        )
                    }
                    return (
                        <StatusBadge $color={badge.color}>
                            {badge.label}
                            {item.LastRunReports != null
                                ? ` · ${item.LastRunReports} reports`
                                : ''}
                        </StatusBadge>
                    )
                },
            },
            {
                title: 'Actions',
                textAlign: 'center',
                width: isMobile ? 'auto' : 130,
                render: item => (
                    <ActionButtons
                        item={item}
                        onEdit={onEdit}
                        onHoldToggle={onHoldToggle}
                        onFireNow={onFireNow}
                    />
                ),
            },
        ],
        [isMobile, timezone, onEdit, onHoldToggle, onFireNow],
    )

    return (
        <AppTable
            data={data}
            columns={columns}
            pageChanged={pageChanged}
            filter={filter}
            totalCount={totalCount}
            mobileCard={
                <ScheduleCard
                    onEdit={onEdit}
                    onHoldToggle={onHoldToggle}
                    onFireNow={onFireNow}
                    timezone={timezone}
                />
            }
        />
    )
}
